import React, { useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import useVoyagerStore from '../../stores/useVoyagerStore';
import WorkspaceComposer from './WorkspaceComposer';
import WorkspaceTurn from './WorkspaceTurn';
import { qbAgentHeader, qbAgentPanel, qbIconButton } from '../VoyagerPanes/queryBuilderPresentation';

const WorkspaceAgentPanel: React.FC = () => {
  const open = useVoyagerStore((s) => s.workspaceAgentPanelOpen);
  const width = useVoyagerStore((s) => s.workspaceAgentPanelWidth);
  const setWidth = useVoyagerStore((s) => s.setWorkspaceAgentPanelWidth);
  const toggle = useVoyagerStore((s) => s.toggleWorkspaceAgentPanel);
  const turns = useVoyagerStore((s) => s.workspaceAgentTurns);
  const clear = useVoyagerStore((s) => s.clearWorkspaceAgentHistory);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [turns]);

  // Drag-to-resize handler
  const startDrag = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = width;
    const onMove = (ev: MouseEvent) => {
      const dx = startX - ev.clientX;
      setWidth(startW + dx);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  if (!open) return null;

  return (
    <Box
      sx={{
        width,
        flexShrink: 0,
        ...qbAgentPanel,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
      component="aside"
      aria-label="Workspace Agent"
      data-testid="qb-agent-panel"
    >
      {/* resize handle */}
      <Box
        onMouseDown={startDrag}
        sx={{
          position: 'absolute',
          left: -3,
          top: 0,
          bottom: 0,
          width: 6,
          cursor: 'col-resize',
          zIndex: 1,
          '&:hover': { bgcolor: 'primary.light', opacity: 0.3 },
        }}
      />

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...qbAgentHeader }}>
        <AutoAwesomeIcon fontSize="small" sx={{ color: 'var(--agent)' }} />
        <Typography variant="subtitle2" sx={{ flex: 1, fontFamily: 'var(--display)', fontSize: 15, fontWeight: 700, letterSpacing: 0, textTransform: 'none', color: 'var(--ink)' }}>
          Workspace Agent
        </Typography>
        <Tooltip title="Clear conversation">
          <span>
            <IconButton size="small" onClick={clear} disabled={turns.length === 0} aria-label="Clear conversation" sx={qbIconButton}>
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Close">
          <IconButton size="small" onClick={toggle} aria-label="Close" sx={qbIconButton}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Scroll area */}
      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 1.75,
          py: 1.75,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.75,
        }}
      >
        {turns.length === 0 ? (
          <Box sx={{ color: 'text.secondary', textAlign: 'center', mt: 4, px: 2 }}>
            <Typography variant="body2" sx={{ fontSize: 13 }}>
              Ask a natural-language question and the agent will propose selections for the Query Builder.
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.disabled' }}>
              Examples: "bookings by country last 30 days" · "explain this perspective"
            </Typography>
          </Box>
        ) : (
          turns.map((t) => <WorkspaceTurn key={t.id} turn={t} />)
        )}
      </Box>

      {/* Composer */}
      <Box sx={{ p: 1, borderTop: '1px solid', borderColor: 'var(--line-soft)', px: 1.5, py: 1.25 }}>
        <WorkspaceComposer />
      </Box>
    </Box>
  );
};

export default WorkspaceAgentPanel;
